import * as React from "react";
import {
  Button,
  cn,
  SegmentedControl,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
  useTheme,
  type Theme,
} from "@aisquare/ui";
import {
  ArrowLeftRightIcon,
  ArrowRightIcon,
  MenuIcon,
  MonitorIcon,
  MoonIcon,
  SearchIcon,
  SunIcon,
} from "lucide-react";
import pkg from "../../package.json";
import { entryForId, neighborsFor } from "./lib/registry";
import { hrefForComponent, routeKey, useRoute, type Route } from "./lib/router";
import { useDirection } from "./lib/theme";
import { Eyebrow, IS_MAC, Kbd } from "./lib/chrome";
import { AISquareMark } from "./lib/AISquareMark";
import { CommandPalette } from "./lib/CommandPalette";
import { Sidebar } from "./lib/Sidebar";
import { LayerIndex, Overview } from "./lib/Overview";

const HEADER_H = "3.5rem"; // 56px — the sticky header, referenced by both rails.

/** Light / dark / system, on the library's own ThemeProvider cascade. */
function ThemeControl() {
  const { theme, setTheme } = useTheme();
  return (
    <SegmentedControl
      size="sm"
      semantics="radiogroup"
      aria-label="Color theme"
      value={theme}
      onChange={(v) => setTheme(v as Theme)}
      options={[
        { value: "light", label: <SunIcon />, "aria-label": "Light theme" },
        { value: "dark", label: <MoonIcon />, "aria-label": "Dark theme" },
        {
          value: "system",
          label: <MonitorIcon />,
          "aria-label": "Follow the system theme",
        },
      ]}
    />
  );
}

/** Prev / next through the whole library, in sidebar order. */
function Pager({ id }: { id: string }) {
  const { prev, next } = neighborsFor(id);
  if (!prev && !next) return null;

  return (
    <nav
      aria-label="Previous and next component"
      className="mt-16 grid gap-3 border-t border-border pt-6 sm:grid-cols-2"
    >
      {prev ? (
        <a
          href={hrefForComponent(prev.id)}
          className="group rounded-lg border border-border bg-card px-4 py-3 transition-colors duration-180 hover:border-accent/40 hover:bg-accent/5"
        >
          <Eyebrow className="mb-1 flex items-center gap-1.5">
            <ArrowRightIcon className="size-3 rotate-180 rtl:rotate-0" />
            Previous
          </Eyebrow>
          <span className="text-label text-foreground">{prev.title}</span>
        </a>
      ) : (
        <span aria-hidden />
      )}
      {next ? (
        <a
          href={hrefForComponent(next.id)}
          className="group rounded-lg border border-border bg-card px-4 py-3 text-end transition-colors duration-180 hover:border-accent/40 hover:bg-accent/5 sm:col-start-2"
        >
          <Eyebrow className="mb-1 flex items-center justify-end gap-1.5">
            Next
            <ArrowRightIcon className="size-3 rtl:rotate-180" />
          </Eyebrow>
          <span className="text-label text-foreground">{next.title}</span>
        </a>
      ) : null}
    </nav>
  );
}

function RouteView({ route }: { route: Route }) {
  if (route.kind === "overview") return <Overview />;
  if (route.kind === "layer") return <LayerIndex id={route.id} />;

  const entry = entryForId(route.id);
  if (!entry) return <Overview />;

  return (
    <div className="pb-4 pt-8">
      <entry.Demo />
      <Pager id={route.id} />
    </div>
  );
}

export default function App() {
  const route = useRoute();
  const { dir, toggleDir } = useDirection();
  const [cmdOpen, setCmdOpen] = React.useState(false);
  const [navOpen, setNavOpen] = React.useState(false);
  const key = routeKey(route);

  // ⌘K / Ctrl+K anywhere opens the palette; "/" does too, the shortcut most
  // component-library docs use, as long as focus isn't already in a field.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      } else if (e.key === "/" && !typing && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // A route change is a new page: start it at the top rather than inheriting
  // the previous page's scroll offset.
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [key]);

  return (
    <div className="min-h-screen">
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />

      <header
        className="sticky top-0 z-40 border-b border-border bg-[hsl(var(--pg-bg)/0.8)] backdrop-blur-xl"
        style={{ height: HEADER_H }}
      >
        <div className="mx-auto flex h-full max-w-[1600px] items-center gap-3 px-4 sm:px-6">
          <Sheet open={navOpen} onOpenChange={setNavOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Open navigation"
                className="lg:hidden"
              >
                <MenuIcon />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[290px] p-0">
              <SheetTitle className="sr-only">Component navigation</SheetTitle>
              <SheetDescription className="sr-only">
                Every component in {pkg.name}, grouped by layer.
              </SheetDescription>
              <Sidebar route={route} onNavigate={() => setNavOpen(false)} />
            </SheetContent>
          </Sheet>

          <a
            href="#/"
            className="group flex min-w-0 items-center gap-2.5"
            aria-label="Playground home"
          >
            <AISquareMark className="h-6 w-auto shrink-0 text-foreground" />
            <span className="truncate font-semibold text-foreground">{pkg.name}</span>
            <span className="hidden shrink-0 rounded-full border border-border bg-muted px-2 py-0.5 font-mono text-[10px] tabular-nums text-muted-foreground sm:inline">
              v{pkg.version}
            </span>
          </a>

          <div className="ms-auto flex items-center gap-2">
            <button
              type="button"
              dir="ltr"
              onClick={() => setCmdOpen(true)}
              aria-label="Search components"
              className={cn(
                "flex h-9 items-center gap-2 rounded-lg border border-input bg-background px-2.5 text-label text-muted-foreground",
                "transition-colors duration-180 hover:border-accent/40 hover:text-foreground md:w-56 md:px-3",
              )}
            >
              <SearchIcon className="size-4 shrink-0" />
              <span className="hidden flex-1 text-start md:inline">Search…</span>
              <Kbd className="ms-auto hidden md:inline-flex">{IS_MAC ? "⌘K" : "Ctrl K"}</Kbd>
            </button>

            <Button
              variant="ghost"
              size="icon-sm"
              onClick={toggleDir}
              aria-label={`Switch to ${dir === "ltr" ? "RTL" : "LTR"}`}
              title={`Direction: ${dir.toUpperCase()}`}
            >
              <ArrowLeftRightIcon />
            </Button>

            <div className="hidden sm:block">
              <ThemeControl />
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1600px]">
        <aside
          className="sticky hidden shrink-0 border-e border-border lg:block lg:w-[272px]"
          style={{ top: HEADER_H, height: `calc(100vh - ${HEADER_H})` }}
        >
          <Sidebar route={route} />
        </aside>

        {/*
          `key` remounts the column on every route change. Deliberate: demos hold
          their own state (open dialogs, running animations, polling timers), and
          reusing the tree across two different components would carry one
          component's state into the next one's page.
        */}
        <main key={key} className="min-w-0 flex-1 px-5 sm:px-8 xl:px-12">
          <div className="mx-auto max-w-[1080px]">
            <RouteView route={route} />
          </div>
        </main>
      </div>
    </div>
  );
}
